import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import AdminHeader from "../../header/admin_header";
import AdminSidebar from "../../sidebar/admin_sidebar";
import ContextBar from "./UI/ContextBar";
import KpiGrid from "./UI/KpiGrid";
import KpiGridRow from "./UI/KpiGridRow";
import ActionBar from "./UI/ActionBar";
import ColumnPicker from "./UI/ColumnPicker";
import ExcelMenu from "./UI/ExcelMenu";
import EsgPillarsMenu from "./UI/EsgPillarsMenu";
import ImportDialog from "./UI/ImportDialog";
import KpiTypeChangeDialog from "./UI/KpiTypeChangeDialog";
import { buildColumns } from "./UI/columns";
import { useKpiCatalogues } from "./model/useKpiCatalogues";
import { useGhgReferences } from "../EmissionFactorBreakdown/useGhgReferences";
import { useKpiRows, PAGE_SIZE } from "./model/useKpiRows";
import { useDebouncedValue } from "./model/useDebouncedValue";
import { useEmissionFactors } from "./model/useEmissionFactors";
import { useRowDrafts } from "./model/useRowDrafts";
import { useRowSelection } from "./model/useRowSelection";
import { useSaveRows } from "./model/useSaveRows";
import { useModuleTagPillars } from "./model/useModuleTagPillars";
import { useKeywordWrites } from "./model/useKeywordWrites";
import { useColumnVisibility } from "./model/useColumnVisibility";
import { useExcelTransfer } from "./model/useExcelTransfer";
import {
  addUnit,
  appendTagTriple,
  applyFactorAction,
  buildFactorPayload,
  buildSavePayload,
  isFixedTagGroup,
  evidenceKindsInUse,
  keywordValuesFor,
  mergeRow,
  removeUnit,
  savedRowsWithKpiTypeEdit,
  setBaseUnit,
  setTagField,
  setUnitFactor,
  supportsAggregation,
  tagTriplesInUse,
  toggleInList,
  validateRows,
} from "./model/helpers";
import {
  AGGREGATION_METHODS,
  EMISSION_SCOPES,
  FACTOR_COUNTRIES,
  FIXED_TAG_GROUPS,
  FREQUENCIES,
  KPI_CATEGORIES,
  KPI_STATUSES,
  QUESTION_TYPES,
  QUESTION_TYPE_VALUES,
  TAG_FIELDS,
} from "./model/options";
import "./KPIGrid.css";

const FIXED_TAG_NAMES = FIXED_TAG_GROUPS.map((group) => group.name);
const COLUMNS = buildColumns({ tagGroups: FIXED_TAG_GROUPS, tagFields: TAG_FIELDS });

const countryCodeOf = (fy) => String(fy.iso_code || fy.country_code || fy.country_name || "").trim();

const yearLabel = (fy) => fy.financial_year_value || fy.year || fy.title || `FY ${fy.id}`;

/** India is where most reporting sits, so it opens selected when it is on offer. */
const defaultCountry = (options) =>
  options.find((option) => /^(in|ind)$/i.test(option.value) || /^india$/i.test(option.label));

/** The newest year: the highest number the label starts with, else the last one listed. */
const latestYear = (options) =>
  options.reduce((best, option) => (startYear(option.label) > startYear(best.label) ? option : best), options[0]);

const startYear = (label) => Number(String(label).match(/\d{4}/)?.[0] ?? 0);

/**
 * KPI Grid Bench — every KPI for one module and financial year on a single
 * sheet, editable in the row it sits in.
 *
 * This file is the seam: it calls the hooks in `model/`, derives what the
 * cells need, and hands it down as props. Nothing in `UI/` imports from
 * `model/`, which is what keeps those components reusable and testable with
 * hand-written objects.
 *
 * The app has no shared layout route, so every page brings its own chrome —
 * sidebar, header, then `.main_wrapper > .inner_wraapper`. Rendering the grid
 * without them is what makes the sidebar vanish.
 */
export default function KPIGrid() {
  const location = useLocation();
  const {
    modules,
    financialYears,
    unitMaster,
    ghgDatabases,
    frameworks,
    industries,
    tagKeys,
    tagValuesByKey,
    addKeyword,
    modulePillars,
    unassignedModuleCount,
    setModulePillar,
    loading: cataloguesLoading,
    error: cataloguesError,
    reload: reloadCatalogues,
  } = useKpiCatalogues();
  const { references, reload: reloadReferences } = useGhgReferences();

  const [moduleIds, setModuleIds] = useState([]);
  /** Distinguishes "not picked yet" from a deliberate "All modules". */
  const [moduleChosen, setModuleChosen] = useState(false);
  const [countryCode, setCountryCode] = useState("");
  const [financialYearIds, setFinancialYearIds] = useState([]);
  const [search, setSearch] = useState("");
  const [includeInactive, setIncludeInactive] = useState(false);
  /** Titles of the KPIs the last save created, to point at any that sorted onto another page. */
  const [justCreated, setJustCreated] = useState([]);
  const pendingCreatedRef = useRef([]);
  const [kpiTypeEditTitles, setKpiTypeEditTitles] = useState(null);
  const [activeTypes, setActiveTypes] = useState([]);
  const [offset, setOffset] = useState(0);
  const debouncedSearch = useDebouncedValue(search);

  const industryOptions = useMemo(
    () =>
      industries.map((industry) => ({
        value: String(industry.id),
        label: industry.industry_type ? `${industry.name} (${industry.industry_type})` : industry.name,
      })),
    [industries],
  );

  const frameworkOptions = useMemo(
    () => frameworks.map((framework) => ({ value: String(framework.id), label: framework.title })),
    [frameworks],
  );

  const moduleOptions = useMemo(
    () => modules.map((m) => ({ value: String(m.id), label: m.title })),
    [modules],
  );

  /**
   * With exactly one module filtered, every visible row belongs to it: the
   * Module cell is locked and new rows inherit it. With several or all, each
   * row picks its own module.
   */
  const moduleLocked = moduleIds.length === 1;
  const singleModuleId = moduleLocked ? moduleIds[0] : "";

  /**
   * A financial year belongs to a country — the same year exists per market —
   * so the country picks which years are on offer rather than every year of
   * every country crowding one list.
   */
  const countryOptions = useMemo(() => {
    const byCode = new Map();
    financialYears.forEach((fy) => {
      const code = countryCodeOf(fy);
      if (code && !byCode.has(code)) byCode.set(code, fy.country_name || code);
    });
    return [...byCode].map(([value, label]) => ({ value, label }));
  }, [financialYears]);

  /** Every year by id, including other countries', so a row always names its own. */
  const financialYearLabels = useMemo(() => {
    const byId = {};
    financialYears.forEach((fy) => {
      byId[String(fy.id)] = yearLabel(fy);
    });
    return byId;
  }, [financialYears]);

  const financialYearOptions = useMemo(
    () =>
      financialYears
        .filter((fy) => !countryCode || countryCodeOf(fy) === countryCode)
        .map((fy) => ({ value: String(fy.id), label: yearLabel(fy) })),
    [financialYears, countryCode],
  );

  /**
   * The grid opens on real rows rather than a shell asking to be configured:
   * the first module, India where it exists, and that country's newest year.
   */
  useEffect(() => {
    if (!moduleChosen && moduleOptions.length) {
      setModuleIds([moduleOptions[0].value]);
      setModuleChosen(true);
    }
    if (!countryCode && countryOptions.length) {
      setCountryCode((defaultCountry(countryOptions) || countryOptions[0]).value);
      return;
    }
    if (!financialYearIds.length && financialYearOptions.length) {
      setFinancialYearIds([latestYear(financialYearOptions).value]);
    }
  }, [moduleChosen, moduleOptions, countryCode, countryOptions, financialYearIds, financialYearOptions]);

  const singleFinancialYearId = financialYearIds.length === 1 ? financialYearIds[0] : "";
  const oneYearOnlyReason = financialYearIds.length === 1 ? "" : "Pick one financial year first";
  const singleFinancialYearLabel =
    financialYearOptions.find((option) => option.value === singleFinancialYearId)?.label || "";

  const queriedTypes = activeTypes.length ? activeTypes : QUESTION_TYPE_VALUES;

  const { rows, total, loading, refreshing, error, refresh, reload } = useKpiRows({
    moduleIds,
    financialYearIds,
    questionTypes: queriedTypes,
    search: debouncedSearch,
    includeInactive,
    offset,
  });

  const savedKpiIds = useMemo(() => rows.map((row) => row.questionId), [rows]);
  const {
    factorsByKpi,
    loading: factorsLoading,
    error: factorsFailed,
    refresh: retryFactors,
  } = useEmissionFactors(savedKpiIds);

  /**
   * Factors live in their own table but edit like any other cell, so they are
   * folded onto the row before the draft machinery sees it. Dirty tracking,
   * discard and validation then cover them with no parallel edit state.
   */
  const rowsWithFactors = useMemo(
    () => rows.map((row) => ({ ...row, factors: factorsByKpi[String(row.questionId)] || [] })),
    [rows, factorsByKpi],
  );

  const {
    patches,
    drafts,
    dirtyCount,
    newCount,
    setField,
    bulkUpdateField,
    addDraft,
    removeDraft,
    discardAll,
  } = useRowDrafts(rowsWithFactors);

  const selection = useRowSelection();
  const { syncTo: syncSelection } = selection;

  const columnVisibility = useColumnVisibility(COLUMNS);
  const visibleColumns = useMemo(
    () => COLUMNS.filter((column) => !columnVisibility.hidden.has(column.key)),
    [columnVisibility.hidden],
  );

  /**
   * After a save the rows and the factors both reload: the factor list is
   * keyed on KPI ids, which a save rarely changes, so it would otherwise keep
   * the pre-save factors — new ones without ids, removed ones still present.
   */
  const onSaved = useCallback(async () => {
    setJustCreated(pendingCreatedRef.current);
    discardAll();
    selection.clear();
    await Promise.all([refresh(), retryFactors()]);
  }, [discardAll, selection, refresh, retryFactors]);

  const { saving, error: saveError, savedMessage, save, dismissError } = useSaveRows({ onSaved });

  /** Lists the workbook's dropdowns and name lookups come from. */
  const excelCatalogue = useMemo(
    () => ({
      modules: moduleOptions,
      units: unitMaster.map((unit) => ({ value: String(unit.id), label: unit.unit })),
      databases: ghgDatabases.map((database) => ({ value: String(database.id), label: database.name })),
      industries: industryOptions,
      frameworks: frameworkOptions,
      tagValuesByKey,
    }),
    [moduleOptions, unitMaster, ghgDatabases, industryOptions, frameworkOptions, tagValuesByKey],
  );

  /** An import can add tag pairs, so the vocabulary reloads with the rows. */
  const onImported = useCallback(async () => {
    selection.clear();
    await Promise.all([refresh(), retryFactors(), reloadCatalogues()]);
  }, [selection, refresh, retryFactors, reloadCatalogues]);

  const excel = useExcelTransfer({
    catalogue: excelCatalogue,
    financialYearId: singleFinancialYearId,
    financialYearLabel: singleFinancialYearLabel,
    filters: { financialYearIds, moduleIds, questionTypes: queriedTypes, search: debouncedSearch, includeInactive },
    onApplied: onImported,
  });


  const allRows = useMemo(() => [...rowsWithFactors, ...drafts], [rowsWithFactors, drafts]);

  const valuesById = useMemo(() => {
    const map = {};
    allRows.forEach((row) => {
      map[row.id] = mergeRow(row, patches[row.id]);
    });
    return map;
  }, [allRows, patches]);

  const errorsById = useMemo(
    () => validateRows(allRows.map((row) => ({ id: row.id, values: valuesById[row.id] }))),
    [allRows, valuesById],
  );

  const errorCount = useMemo(() => Object.keys(errorsById).length, [errorsById]);

  const visibleIds = useMemo(() => allRows.map((row) => row.id), [allRows]);

  const valuesByIdRef = useRef(valuesById);
  valuesByIdRef.current = valuesById;
  const currentValueOf = useCallback((rowId, field) => valuesByIdRef.current[rowId]?.[field], []);

  const onToggleChip = useCallback(
    (rowId, field, value) => {
      setField(rowId, field, toggleInList(currentValueOf(rowId, field) || [], value));
    },
    [setField, currentValueOf],
  );

  /**
   * Unit edits are conversion-table edits, so each action goes through the
   * helper that keeps the table coherent — switching the base resets its
   * factor to 1, adding the first unit makes it the base.
   */
  const onUnitAction = useCallback(
    (rowId, action) => {
      const units = currentValueOf(rowId, "units") || [];
      const next =
        action.type === "add"
          ? addUnit(units, action.unitId)
          : action.type === "remove"
            ? removeUnit(units, action.unitId)
            : action.type === "base"
              ? setBaseUnit(units, action.unitId)
              : setUnitFactor(units, action.unitId, action.factor);
      setField(rowId, "units", next);
    },
    [setField, currentValueOf],
  );


  const lastFactorRef = useRef(null);

  const unitNameOf = useCallback(
    (unitId) => unitMaster.find((unit) => Number(unit.id) === Number(unitId))?.unit || "",
    [unitMaster],
  );

  const onFactorAction = useCallback(
    (rowId, action) => {
      if (factorsFailed) return;
      const next = applyFactorAction(
        currentValueOf(rowId, "factors") || [],
        currentValueOf(rowId, "units") || [],
        action.type === "add" ? { ...action, defaults: lastFactorRef.current } : action,
        unitNameOf,
      );
      if (action.type === "change") lastFactorRef.current = next[action.index];
      setField(rowId, "factors", next);
    },
    [setField, currentValueOf, factorsFailed, unitNameOf],
  );

  const tagOptions = useMemo(() => {
    const byField = {};
    TAG_FIELDS.forEach((field) => {
      byField[field.key] = keywordValuesFor(tagValuesByKey, field.key);
    });
    return byField;
  }, [tagValuesByKey]);

  const onTagField = useCallback(
    (rowId, groupName, index, value) => {
      setField(rowId, "tagGroups", setTagField(currentValueOf(rowId, "tagGroups") || [], groupName, index, value));
    },
    [setField, currentValueOf],
  );


  const pillarListing = useModuleTagPillars();
  const { fileTagValue, setPillar: onSetModulePillar } = useKeywordWrites({
    tagValuesByKey,
    addKeyword,
    setModulePillar,
    applyPillar: pillarListing.applyPillar,
  });

  const onCreateTagValue = useCallback(
    (rowId, groupName, index, fieldKey, value) => {
      const trimmed = String(value).trim();
      if (!trimmed) return;
      onTagField(rowId, groupName, index, trimmed);
      fileTagValue(fieldKey, trimmed);
    },
    [onTagField, fileTagValue],
  );

  const onOtherTags = useCallback(
    (rowId, otherGroups) => {
      const fixed = (currentValueOf(rowId, "tagGroups") || []).filter(isFixedTagGroup);
      setField(rowId, "tagGroups", [...fixed, ...otherGroups]);
    },
    [setField, currentValueOf],
  );

  const onRemoveDraft = useCallback(
    (rowId) => {
      removeDraft(rowId);
      syncSelection(visibleIds.filter((id) => id !== rowId));
    },
    [removeDraft, syncSelection, visibleIds],
  );

  const onSeedRow = useCallback(
    (title) => addDraft(title, { moduleId: singleModuleId }),
    [addDraft, singleModuleId],
  );

  const onToggleAll = useCallback(
    (selected) => selection.setAll(visibleIds, selected),
    [selection, visibleIds],
  );

  const resetPaging = useCallback(() => {
    setOffset(0);
    selection.clear();
  }, [selection]);

  const onSearchChange = useCallback(
    (value) => {
      setSearch(value);
      resetPaging();
    },
    [resetPaging],
  );

  const onIncludeInactiveChange = useCallback(
    (value) => {
      setIncludeInactive(value);
      resetPaging();
    },
    [resetPaging],
  );

  /**
   * The grid sorts A–Z in pages, so a KPI just created can land on another
   * page and look lost. Only those are offered, and only one at a time.
   */
  const createdOutOfView = useMemo(() => {
    const onPage = new Set(rows.map((row) => String(row.title).trim().toLowerCase()));
    return justCreated.filter((title) => !onPage.has(title.toLowerCase()));
  }, [justCreated, rows]);

  const onTypesChange = useCallback(
    (values) => {
      setActiveTypes(values);
      resetPaging();
    },
    [resetPaging],
  );

  /**
   * Switching module or year replaces every row, so the selects stay locked
   * while edits are pending — the action bar says to save or discard first.
   * Nothing is silently thrown away and no dialog has to interrupt.
   */
  const onModulesChange = useCallback(
    (values) => {
      setModuleIds(values);
      setModuleChosen(true);
      resetPaging();
    },
    [resetPaging],
  );

  /** A different country offers different years, so the newest of the new list is taken. */
  const onCountryChange = useCallback(
    (value) => {
      setCountryCode(value);
      setFinancialYearIds([]);
      resetPaging();
    },
    [resetPaging],
  );

  const onFinancialYearsChange = useCallback(
    (values) => {
      setFinancialYearIds(values);
      resetPaging();
    },
    [resetPaging],
  );


  const evidenceCatalogue = useMemo(() => evidenceKindsInUse(rows), [rows]);

  const tagTripleOptions = useMemo(
    () => tagTriplesInUse(Object.values(valuesById)).map((t) => ({ value: t.id, label: t.label })),
    [valuesById],
  );


  const onPage = useCallback(
    (nextOffset) => {
      setOffset(Math.max(nextOffset, 0));
      selection.clear();
    },
    [selection],
  );

  const bulkControls = useMemo(
    () => [
      { key: "moduleId", label: "Module…", options: moduleOptions },
      { key: "type", label: "Type…", options: QUESTION_TYPES },
      { key: "frequency", label: "Frequency…", options: FREQUENCIES },
      { key: "aggregation", label: "Aggregation…", options: AGGREGATION_METHODS },
      { key: "category", label: "Category…", options: KPI_CATEGORIES },
      { key: "tagTriple", label: "Add tag…", options: tagTripleOptions },
      {
        key: "evidence",
        label: "Add evidence…",
        options: evidenceCatalogue.map((kind) => ({ value: kind, label: kind })),
      },
    ],
    [tagTripleOptions, moduleOptions, evidenceCatalogue],
  );

  const onBulk = useCallback(
    (key, value) => {
      if (!value) return;
      const ids = selection.selectedArray;

      if (key === "tagTriple") {
        const [group, pairKey, ...rest] = value.split("|");
        const triple = { group: group || null, key: pairKey, value: rest.join("|") };
        bulkUpdateField(ids, "tagGroups", (rowId) =>
          appendTagTriple(currentValueOf(rowId, "tagGroups") || [], triple),
        );
        return;
      }

      if (key === "evidence") {
        bulkUpdateField(ids, "evidences", (rowId) => {
          const list = currentValueOf(rowId, "evidences") || [];
          return list.includes(value) ? list : [...list, value];
        });
        return;
      }

      bulkUpdateField(ids, key, () => value);
    },
    [selection.selectedArray, bulkUpdateField, currentValueOf],
  );

  const saveAll = useCallback(() => {
    setKpiTypeEditTitles(null);
    pendingCreatedRef.current = drafts.map((draft) => String(valuesById[draft.id].title).trim());
    save(buildSavePayload({ rows: allRows, patches, moduleId: singleModuleId, financialYearId: singleFinancialYearId }), (resolveKpiId) =>
      buildFactorPayload({ rows: allRows, patches, resolveKpiId }),
    );
  }, [save, allRows, patches, singleModuleId, singleFinancialYearId, drafts, valuesById]);

  const onSave = useCallback(() => {
    if (errorCount > 0) return;
    const kpiTypeEdits = savedRowsWithKpiTypeEdit(allRows, patches);
    if (kpiTypeEdits.length) {
      setKpiTypeEditTitles(kpiTypeEdits.map((row) => String(valuesById[row.id].title).trim()));
      return;
    }
    saveAll();
  }, [errorCount, allRows, patches, valuesById, saveAll]);

  const onCancelKpiTypeEdit = useCallback(() => setKpiTypeEditTitles(null), []);

  const onDiscard = useCallback(() => {
    discardAll();
    selection.clear();
  }, [discardAll, selection]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== "s") return;
      e.preventDefault();
      if (dirtyCount > 0 && errorCount === 0 && !saving) onSave();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [dirtyCount, errorCount, saving, onSave]);

  const selectedVisible = visibleIds.filter((id) => selection.selectedIds.has(id));

  return (
    <div>
      <AdminSidebar dataFromParent={location.pathname} />
      <AdminHeader />
      <div className="main_wrapper">
        <div className="inner_wraapper">
          <div className="kgb-root">
            <h1 className="kgb-title">KPI Grid</h1>

            <div className="kgb-card">
              <ContextBar
                countryOptions={countryOptions}
                countryCode={countryCode}
                financialYearOptions={financialYearOptions}
                financialYearIds={financialYearIds}
                moduleOptions={moduleOptions}
                moduleIds={moduleIds}
                search={search}
                includeInactive={includeInactive}
                onIncludeInactiveChange={onIncludeInactiveChange}
                typeOptions={QUESTION_TYPES}
                activeTypes={activeTypes}
                rowCount={allRows.length}
                total={total}
                offset={offset}
                pageSize={PAGE_SIZE}
                busy={refreshing}
                locked={dirtyCount > 0}
                onPage={onPage}
                onCountryChange={onCountryChange}
                onFinancialYearsChange={onFinancialYearsChange}
                onModulesChange={onModulesChange}
                onSearchChange={onSearchChange}
                onTypesChange={onTypesChange}
              >
                <ExcelMenu
                  busy={excel.busy}
                  uploadLocked={dirtyCount > 0}
                  lockedReason={oneYearOnlyReason}
                  onDownload={excel.download}
                  onUpload={excel.preview}
                />
                <EsgPillarsMenu
                  unassignedCount={pillarListing.loaded ? pillarListing.unsetCount : unassignedModuleCount}
                  rows={pillarListing.rows}
                  groups={FIXED_TAG_GROUPS}
                  loading={pillarListing.loading}
                  error={pillarListing.error}
                  onLoad={pillarListing.load}
                  onSetPillar={onSetModulePillar}
                />
                <ColumnPicker
                  columns={COLUMNS}
                  hidden={columnVisibility.hidden}
                  onToggle={columnVisibility.toggle}
                  onShowAll={columnVisibility.showAll}
                />
              </ContextBar>

              <KpiGrid
                columns={visibleColumns}
                loading={loading || cataloguesLoading}
                error={error || cataloguesError}
                onRetry={error ? reload : reloadCatalogues}
                isEmpty={allRows.length === 0}
                hasRows={rows.length > 0}
                allSelected={selectedVisible.length > 0 && selectedVisible.length === visibleIds.length}
                someSelected={selectedVisible.length > 0}
                onToggleAll={onToggleAll}
                addDisabled={!!oneYearOnlyReason}
                onSeedRow={onSeedRow}
              >
                {allRows.map((row) => {
                  const values = valuesById[row.id];
                  const rowErrors = errorsById[row.id];
                  return (
                    <KpiGridRow
                      key={row.id}
                      columns={visibleColumns}
                      row={row}
                      patch={patches[row.id]}
                      isNew={row.questionId == null}
                      selected={selection.selectedIds.has(row.id)}
                      titleError={rowErrors?.title}
                      moduleError={rowErrors?.moduleId}
                      aggregationError={rowErrors?.aggregation}
                      unitsError={rowErrors?.units}
                      factorsError={rowErrors?.factors}
                      variablesError={rowErrors?.variables}
                      detailsError={rowErrors?.details}
                      aggregationDisabled={!supportsAggregation(values.type)}
                      factorsLoading={factorsLoading}
                      factorsFailed={factorsFailed}
                      onRetryFactors={retryFactors}
                      scopeOptions={EMISSION_SCOPES}
                      countrySuggestions={FACTOR_COUNTRIES}
                      ghgDatabases={ghgDatabases}
                      references={references}
                      onReferencesChanged={reloadReferences}
                      moduleOptions={moduleOptions}
                      financialYearLabel={financialYearLabels[values.financialYearId] || ""}
                      typeOptions={QUESTION_TYPES}
                      frequencyOptions={FREQUENCIES}
                      aggregationOptions={AGGREGATION_METHODS}
                      unitMaster={unitMaster}
                      tagKeys={tagKeys}
                      fixedTagGroups={FIXED_TAG_NAMES}
                      tagOptions={tagOptions}
                      tagValuesByKey={tagValuesByKey}
                      modulePillars={modulePillars}
                      evidenceCatalogue={evidenceCatalogue}
                      industryOptions={industryOptions}
                      frameworkOptions={frameworkOptions}
                      kpiStatusOptions={KPI_STATUSES}
                      categoryOptions={KPI_CATEGORIES}
                      onField={setField}
                      onToggleChip={onToggleChip}
                      onUnitAction={onUnitAction}
                      onFactorAction={onFactorAction}
                      onToggleSelect={selection.toggle}
                      onRemoveDraft={onRemoveDraft}
                      onTagField={onTagField}
                      onCreateTagValue={onCreateTagValue}
                      onFileTagValue={fileTagValue}
                      onSetModulePillar={onSetModulePillar}
                      onOtherTags={onOtherTags}
                    />
                  );
                })}
              </KpiGrid>

              <ActionBar
                dirtyCount={dirtyCount}
                newCount={newCount}
                selectedCount={selection.selectedCount}
                errorCount={errorCount}
                saving={saving}
                savedMessage={savedMessage}
                saveError={saveError}
                bulkControls={bulkControls}
                onBulk={onBulk}
                onClearSelection={selection.clear}
                onDiscard={onDiscard}
                onSave={onSave}
                onDismissError={dismissError}
              >
                {createdOutOfView.length === 1 ? (
                  <button
                    type="button"
                    className="kgb-add-link"
                    onClick={() => onSearchChange(createdOutOfView[0])}
                  >
                    Show “{createdOutOfView[0]}”
                  </button>
                ) : null}
                {createdOutOfView.length > 1 ? (
                  <span className="kgb-emission--muted">
                    {createdOutOfView.length} new KPIs are on other pages (A–Z) — search to find them
                  </span>
                ) : null}
              </ActionBar>
            </div>

            <KpiTypeChangeDialog titles={kpiTypeEditTitles} onConfirm={saveAll} onCancel={onCancelKpiTypeEdit} />

            <ImportDialog
              plan={excel.plan}
              result={excel.result}
              error={excel.error}
              busy={excel.busy}
              financialYearLabel={singleFinancialYearLabel}
              onApply={excel.apply}
              onClose={excel.close}
            />

            <ul className="kgb-legend">
              <li>
                <i className="kgb-rail kgb-rail--dirty" /> Edited
              </li>
              <li>
                <i className="kgb-rail kgb-rail--new" /> New
              </li>
              <li>
                <i className="kgb-rail kgb-rail--invalid" /> Needs fixing
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
